import React, { useRef, useState } from 'react';
import { CameraIcon, GripVerticalIcon, RotateCwIcon, ScanIcon, Trash2Icon, UploadCloudIcon } from 'lucide-react';
import { MiniaturaDocumento } from './MiniaturaDocumento';
import type { OrigenArchivo, PaginaDocumento } from '../../types';
const ORIGENES: {
  clave: OrigenArchivo;
  etiqueta: string;
  ayuda: string;
  icono: typeof ScanIcon;
  oculto?: boolean;
}[] = [{
  clave: 'Archivo',
  etiqueta: 'Subir archivo',
  ayuda: 'Arrastra y suelta PDF, JPG o PNG',
  icono: UploadCloudIcon
}, {
  clave: 'Cámara',
  etiqueta: 'Tomar foto',
  ayuda: 'Cámara de móvil o tablet',
  icono: CameraIcon
},
// Opción oculta por ahora; el flujo de escaneo sigue disponible en el código.
{
  clave: 'Escáner',
  etiqueta: 'Desde escáner',
  ayuda: 'Multifuncional de la sucursal',
  icono: ScanIcon,
  oculto: true
}];
const ORIGENES_VISIBLES = ORIGENES.filter(o => !o.oculto);
export function PasoOrigen({
  origen,
  setOrigen,
  paginas,
  setPaginas
}: {
  origen: OrigenArchivo;
  setOrigen: (o: OrigenArchivo) => void;
  paginas: PaginaDocumento[];
  setPaginas: (p: PaginaDocumento[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [sobreZona, setSobreZona] = useState(false);
  const [hojasEscaner, setHojasEscaner] = useState(3);
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const agregar = (nuevas: PaginaDocumento[]) => setPaginas([...paginas, ...nuevas]);
  const desdeArchivos = (archivos: FileList | null) => {
    if (!archivos) return;
    const nuevas = Array.from(archivos).map((f, i) => ({
      id: `pg-${Date.now()}-${i}`,
      nombre: f.name,
      rotacion: 0,
      origen: 'Archivo' as OrigenArchivo,
      url: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined
    }));
    agregar(nuevas);
  };
  const tomarFoto = () => agregar([{
    id: `pg-${Date.now()}`,
    nombre: `Foto_${String(paginas.length + 1).padStart(2, '0')}.jpg`,
    rotacion: 0,
    origen: 'Cámara'
  }]);
  const escanear = () => agregar(Array.from({
    length: hojasEscaner
  }).map((_, i) => ({
    id: `pg-${Date.now()}-${i}`,
    nombre: `Escaneo_${String(paginas.length + i + 1).padStart(2, '0')}.pdf`,
    rotacion: 0,
    origen: 'Escáner' as OrigenArchivo
  })));
  const rotar = (id: string) => setPaginas(paginas.map(p => p.id === id ? {
    ...p,
    rotacion: (p.rotacion + 90) % 360
  } : p));
  const eliminar = (id: string) => setPaginas(paginas.filter(p => p.id !== id));
  const soltar = (indice: number) => {
    if (arrastrando === null || arrastrando === indice) {
      setArrastrando(null);
      return;
    }
    const lista = [...paginas];
    const [movida] = lista.splice(arrastrando, 1);
    lista.splice(indice, 0, movida);
    setPaginas(lista);
    setArrastrando(null);
  };
  return <div className="space-y-5" data-id="element-336" data-mp="3oxh2y87sj9et">
      <div className="grid gap-3 sm:grid-cols-2" data-id="element-337" data-mp="3oys74955ej6g">
        {ORIGENES_VISIBLES.map(({
        clave,
        etiqueta,
        ayuda,
        icono: Icono
      }) => <button key={clave} type="button" onClick={() => setOrigen(clave)} aria-pressed={origen === clave} className={`rounded-lg border px-4 py-3 text-left transition-colors duration-150 ${origen === clave ? 'border-marino-700 bg-marino-50' : 'border-gris-200 bg-white hover:border-marino-300'}`} data-id="element-338" data-mp="3cly6d6pk0or6">
            <Icono className={`h-5 w-5 ${origen === clave ? 'text-marino-700' : 'text-gris-400'}`} aria-hidden="true" data-id="element-339" data-mp="3ve93ejxj1shd" />
            <p className="mt-2 text-sm font-semibold text-marino-900" data-id="element-340" data-mp="3mcjatarwsni0">{etiqueta}</p>
            <p className="text-2xs text-gris-500" data-id="element-341" data-mp="2fw7b359fuxkb">{ayuda}</p>
          </button>)}
      </div>

      {origen === 'Archivo' && <div onDragOver={e => {
      e.preventDefault();
      setSobreZona(true);
    }} onDragLeave={() => setSobreZona(false)} onDrop={e => {
      e.preventDefault();
      setSobreZona(false);
      desdeArchivos(e.dataTransfer.files);
    }} className={`rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors duration-150 ${sobreZona ? 'border-marino-600 bg-marino-50' : 'border-gris-300 bg-gris-50'}`} data-id="element-342" data-mp="2awumog2x5ifm">
          <UploadCloudIcon className="mx-auto h-7 w-7 text-gris-400" aria-hidden="true" data-id="element-343" data-mp="lct5pcirjibt" />
          <p className="mt-2 text-sm font-medium text-gris-700" data-id="element-344" data-mp="qd7eiws6uwaw">
            Arrastra aquí los archivos del documento
          </p>
          <p className="mt-0.5 text-2xs text-gris-500" data-id="element-345" data-mp="hdap2sxz5tes">
            PDF, JPG o PNG · puedes subir varias páginas y se agrupan en un solo documento
          </p>
          <button type="button" onClick={() => inputRef.current?.click()} className="mt-3 h-9 rounded border border-gris-300 bg-white px-3.5 text-sm font-medium text-marino-700 transition-colors duration-150 hover:border-marino-400" data-id="element-346" data-mp="2x358qq7ve3gm">
            Seleccionar archivos
          </button>
          <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => desdeArchivos(e.target.files)} data-id="element-347" data-mp="33kounjxhp3vq" />
        </div>}

      {origen === 'Cámara' && <div className="rounded-lg border border-gris-200 bg-gris-800 px-6 py-8 text-center" data-id="element-348" data-mp="2atoph5nf9u2h">
          <div className="mx-auto flex h-32 w-48 items-center justify-center rounded border-2 border-dashed border-gris-500" data-id="element-349" data-mp="25v3ov549d8di">
            <CameraIcon className="h-8 w-8 text-gris-400" aria-hidden="true" data-id="element-350" data-mp="2fve0ilana7qk" />
          </div>
          <p className="mt-3 text-xs text-gris-200" data-id="element-351" data-mp="1s5a2ctg8706y">
            Encuadra el documento sobre una superficie plana y con buena luz.
          </p>
          <button type="button" onClick={tomarFoto} className="mt-3 h-9 rounded bg-white px-4 text-sm font-semibold text-marino-800 transition-colors duration-150 hover:bg-gris-100" data-id="element-352" data-mp="1n5xf47lfzr3u">
            Capturar página
          </button>
        </div>}

      {origen === 'Escáner' && <div className="flex flex-wrap items-end gap-4 rounded-lg border border-gris-200 bg-gris-50 px-5 py-4" data-id="element-353" data-mp="vhngpkit09b">
          <label data-id="element-354" data-mp="349u67hrnz5e0">
            <span className="block text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-355" data-mp="34adpb1t1vztm">
              Hojas en el alimentador
            </span>
            <input type="number" min={1} max={40} value={hojasEscaner} onChange={e => setHojasEscaner(Math.max(1, Number(e.target.value)))} className="tabular mt-1.5 h-9 w-28 rounded border border-gris-200 px-3 text-sm text-gris-800" data-id="element-356" data-mp="39b1ctdi2lyj3" />
          </label>
          <div className="flex-1 text-2xs text-gris-500" data-id="element-357" data-mp="xil6nk2w2if2">
            Escáner detectado: <span className="font-medium text-gris-700" data-id="element-358" data-mp="17n1ky90pzrgr">Kyocera TASKalfa —</span>{' '}
            recepción de la sucursal · 300 dpi, escala de grises
          </div>
          <button type="button" onClick={escanear} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-359" data-mp="12qb6fq4kd3xg">
            <ScanIcon className="h-4 w-4" aria-hidden="true" data-id="element-360" data-mp="oxuycawt2fpo" />
            Escanear lote
          </button>
        </div>}

      <div data-id="element-361" data-mp="jwfcwhqpu76r">
        <div className="flex items-center justify-between" data-id="element-362" data-mp="jzthzxqrkuhw">
          <h3 className="text-sm font-semibold text-marino-900" data-id="element-363" data-mp="ezp1w8trq1s7">
            Páginas del documento{' '}
            <span className="tabular font-normal text-gris-500" data-id="element-364" data-mp="aydgkfhxg2pt">({paginas.length})</span>
          </h3>
          {paginas.length > 0 && <button type="button" onClick={() => setPaginas([])} className="text-xs font-medium text-gris-500 hover:text-peligro-600" data-id="element-365" data-mp="29ac4823c59sr">
              Quitar todas
            </button>}
        </div>

        {paginas.length === 0 ? <p className="mt-2 rounded border border-dashed border-gris-300 px-4 py-6 text-center text-xs text-gris-500" data-id="element-366" data-mp="2sn98tslaqfab">
            Aún no hay páginas. Sube, fotografía o escanea al menos una para continuar.
          </p> : <ul className="mt-3 flex flex-wrap gap-3" data-id="element-367" data-mp="24vkniw6w6r2v">
            {paginas.map((p, i) => <li key={p.id} draggable onDragStart={() => setArrastrando(i)} onDragOver={e => e.preventDefault()} onDrop={e => {
          e.preventDefault();
          soltar(i);
        }} onDragEnd={() => setArrastrando(null)} className={`w-32 rounded-lg border bg-white p-2 transition-colors duration-150 ${arrastrando === i ? 'border-marino-500 opacity-60' : 'border-gris-200'}`} data-id="element-368" data-mp="29vgiy71ixa6t" data-mpi={i}>
                <div className="flex items-center justify-between" data-id="element-369" data-mp="47pugi7ytss1">
                  <span className="tabular text-2xs font-semibold text-gris-500" data-id="element-370" data-mp="49kg5yr29ox6">
                    Pág. {i + 1}
                  </span>
                  <GripVerticalIcon className="h-3.5 w-3.5 cursor-grab text-gris-300 active:cursor-grabbing" aria-hidden="true" data-id="element-371" data-mp="d8ny2zehrya3" />
                </div>
                <div className="mt-1.5 flex justify-center" data-id="element-372" data-mp="myqrh0pize8y">
                  <MiniaturaDocumento semilla={p.id} origen={p.origen} rotacion={p.rotacion} url={p.url} ancho={72} alto={94} etiqueta={p.nombre} data-id="element-373" data-mp="n0bkglw6yp4p" />
                </div>
                <p className="mt-1.5 truncate text-2xs text-gris-500" title={p.nombre} data-id="element-374" data-mp="2yo1hi4e232te">
                  {p.nombre}
                </p>
                <div className="mt-1.5 flex items-center justify-between" data-id="element-375" data-mp="2r2wa9nec1845">
                  <button type="button" onClick={() => rotar(p.id)} aria-label={`Rotar página ${i + 1}`} className="flex h-7 w-7 items-center justify-center rounded border border-gris-200 text-gris-500 transition-colors duration-150 hover:border-marino-300 hover:text-marino-700" data-id="element-376" data-mp="2n09p25c62lfx">
                    <RotateCwIcon className="h-3.5 w-3.5" data-id="element-377" data-mp="35r98kr0w731t" />
                  </button>
                  <span className="tabular text-2xs text-gris-400" data-id="element-378" data-mp="35tdc37uvq01k">{p.rotacion}°</span>
                  <button type="button" onClick={() => eliminar(p.id)} aria-label={`Eliminar página ${i + 1}`} className="flex h-7 w-7 items-center justify-center rounded border border-gris-200 text-gris-500 transition-colors duration-150 hover:border-peligro-200 hover:text-peligro-600" data-id="element-379" data-mp="30y7o83hx2bau">
                    <Trash2Icon className="h-3.5 w-3.5" data-id="element-380" data-mp="3w2ugevwxlivq" />
                  </button>
                </div>
              </li>)}
          </ul>}
        {paginas.length > 1 && <p className="mt-2 text-2xs text-gris-400" data-id="element-381" data-mp="9si44oh5w25c">
            Arrastra las miniaturas para reordenar. Las {paginas.length} páginas se guardarán como un
            solo documento.
          </p>}
      </div>
    </div>;
}